import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';

const Profile = () => {
  const { currentUser: user, updateProfile: updateUser, uploadSignature, uploadStamp, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [signature, setSignature] = useState(null);
  const [stamp, setStamp] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState('');
  const [stampPreview, setStampPreview] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [interim, setInterim] = useState({ userId: '', startDate: '', endDate: '' });

  useEffect(() => {
    console.log('Profile useEffect - user:', user);
    console.log('Profile useEffect - authLoading:', authLoading);
    
    if (user) {
      console.log('User ID:', user.id);
      setFormData({
        name: user.name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      // Une signature/cachet est soit une image en base (data:image/...;base64,...),
      // à utiliser telle quelle, soit un chemin de fichier (uploads/...), à préfixer
      // par «/». Préfixer une URL data: la casse (icône d'image indisponible).
      const versApercu = (v) => (v.startsWith('data:') ? v : `/${v}`);
      if (user.signatureUrl) {
        setSignaturePreview(versApercu(user.signatureUrl));
      }

      if (user.stampUrl) {
        setStampPreview(versApercu(user.stampUrl));
      }
    }
  }, [user, authLoading]);

  // Intérim : seule la directrice le configure, on ne charge la liste que pour elle.
  useEffect(() => {
    if (!user || user.role !== 'directrice_marketing') return;

    const toDateInput = (value) => (value ? new Date(value).toISOString().slice(0, 10) : '');
    setInterim({
      userId: user.interim?.userId || '',
      startDate: toDateInput(user.interim?.startDate),
      endDate: toDateInput(user.interim?.endDate),
    });

    api
      .get('/api/users')
      .then((response) => {
        const all = response.data.data || [];
        // Les comptes distributeurs sont des tiers : jamais intérimaires.
        setCandidates(all.filter((u) => u.isActive !== false && u.role !== 'distributeur' && u._id !== (user.id || user._id)));
      })
      .catch(() => toast.error('Impossible de charger la liste des utilisateurs'));
  }, [user]);

  const saveInterim = async (payload) => {
    setLoading(true);
    try {
      await api.put('/api/users/me/interim', payload);
      toast.success(payload.userId ? 'Intérim enregistré' : 'Intérim retiré');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de l\'enregistrement de l\'intérim');
    } finally {
      setLoading(false);
    }
  };

  const handleInterimChange = (e) => {
    setInterim({ ...interim, [e.target.name]: e.target.value });
  };

  const handleInterimSubmit = (e) => {
    e.preventDefault();
    saveInterim(interim);
  };

  const handleInterimClear = () => {
    setInterim({ userId: '', startDate: '', endDate: '' });
    saveInterim({ userId: null });
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const validateImageFile = (file, type) => {
    // Check file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(`Type de fichier non supporté pour ${type}. Formats acceptés: JPG, PNG`);
      return false;
    }
    
    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(`Le fichier ${type} est trop volumineux. Taille maximale: 2MB`);
      return false;
    }
    
    return true;
  };

  const handleSignatureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validateImageFile(file, 'signature')) {
        setSignature(file);
        const reader = new FileReader();
        reader.onload = () => {
          setSignaturePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // Reset the input
        e.target.value = '';
      }
    }
  };

  const handleStampChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (validateImageFile(file, 'cachet')) {
        setStamp(file);
        const reader = new FileReader();
        reader.onload = () => {
          setStampPreview(reader.result);
        };
        reader.readAsDataURL(file);
      } else {
        // Reset the input
        e.target.value = '';
      }
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      await updateUser({
        name: formData.name,
        email: formData.email,
      });
      
      setLoading(false);
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour du profil');
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    try {
      setLoading(true);
      
      await api.put('/api/auth/change-password', {
        currentPassword: formData.currentPassword,
        newPassword: formData.newPassword,
      });
      
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      
      toast.success('Mot de passe mis à jour avec succès');
      setLoading(false);
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.error || 'Erreur lors du changement de mot de passe');
      setLoading(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    e.preventDefault();
    
    if (!signature) {
      toast.error('Veuillez sélectionner une image de signature');
      return;
    }
    
    if (!user || (!user.id && !user._id)) {
      toast.error('Utilisateur non connecté. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      setLoading(true);
      
      await uploadSignature(signature);
      setSignature(null);
      setLoading(false);
    } catch (error) {
      console.error('Error uploading signature:', error);
      setLoading(false);
    }
  };

  const handleStampUpload = async (e) => {
    e.preventDefault();
    
    if (!stamp) {
      toast.error('Veuillez sélectionner une image de cachet');
      return;
    }
    
    if (!user || (!user.id && !user._id)) {
      toast.error('Utilisateur non connecté. Veuillez vous reconnecter.');
      return;
    }
    
    try {
      setLoading(true);
      
      await uploadStamp(stamp);
      setStamp(null);
      setLoading(false);
    } catch (error) {
      console.error('Error uploading stamp:', error);
      setLoading(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Profil</h1>

      {user.actingInterim && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
          <svg className="h-6 w-6 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-semibold text-amber-800">Directeur / directrice par intérim</p>
            <p className="text-sm text-amber-700">
              Vous validez l'étape Direction à la place de la directrice
              {user.actingInterim.endDate
                ? ` jusqu'au ${new Date(user.actingInterim.endDate).toLocaleDateString('fr-FR')}`
                : ''}.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Informations personnelles</h2>
          <form onSubmit={handleProfileUpdate}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="name">
                Nom
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="email">
                Email
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Rôle
              </label>
              <div className="py-2 px-3 bg-gray-100 rounded">
                {user.role === 'directrice_marketing' ? 'Directrice Marketing' :
                 user.role === 'marketing' ? 'Marketing' :
                 user.role === 'force_de_vente' ? 'Force de Vente' :
                 user.role === 'commercial' ? 'Commercial' : user.role}
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2">
                Département
              </label>
              <div className="py-2 px-3 bg-gray-100 rounded">
                {user.department === 'marketing' ? 'Marketing' :
                 user.department === 'force_de_vente' ? 'Force de Vente' :
                 user.department === 'commercial' ? 'Commercial' :
                 user.department === 'direction' ? 'Direction' : user.department}
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Mise à jour...' : 'Mettre à jour'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Change Password */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Changer le mot de passe</h2>
          <form onSubmit={handlePasswordChange}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="currentPassword">
                Mot de passe actuel
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="currentPassword"
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="newPassword">
                Nouveau mot de passe
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="newPassword"
                type="password"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="confirmPassword">
                Confirmer le mot de passe
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="confirmPassword"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </div>
            <div className="flex items-center justify-end">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Changement...' : 'Changer le mot de passe'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Signature : le commercial n'en a pas, la note d'avoir n'est pas signée. */}
        {user.role !== 'commercial' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Signature</h2>
          <form onSubmit={handleSignatureUpload}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="signature">
                Télécharger une signature
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="signature"
                type="file"
                accept="image/*"
                onChange={handleSignatureChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Formats acceptés: JPG, PNG. Taille max: 2MB
              </p>
            </div>
            {signaturePreview && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Aperçu
                </label>
                <div className="border rounded p-2 bg-gray-50">
                  <img
                    src={signaturePreview}
                    alt="Signature Preview"
                    className="max-h-32 mx-auto"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="submit"
                disabled={loading || !signature}
              >
                {loading ? 'Téléchargement...' : 'Télécharger'}
              </button>
            </div>
          </form>
        </div>
        )}
        
        {/* Cachet : ni les animateurs (signature seule) ni le commercial. */}
        {!['animateur', 'commercial'].includes(user.role) && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">Cachet</h2>
          <form onSubmit={handleStampUpload}>
            <div className="mb-4">
              <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="stamp">
                Télécharger un cachet
              </label>
              <input
                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                id="stamp"
                type="file"
                accept="image/*"
                onChange={handleStampChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Formats acceptés: JPG, PNG. Taille max: 2MB
              </p>
            </div>
            {stampPreview && (
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2">
                  Aperçu
                </label>
                <div className="border rounded p-2 bg-gray-50">
                  <img
                    src={stampPreview}
                    alt="Stamp Preview"
                    className="max-h-32 mx-auto"
                  />
                </div>
              </div>
            )}
            <div className="flex items-center justify-end">
              <button
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                type="submit"
                disabled={loading || !stamp}
              >
                {loading ? 'Téléchargement...' : 'Télécharger'}
              </button>
            </div>
          </form>
        </div>
        )}

        {/* Intérim de la directrice marketing */}
        {user.role === 'directrice_marketing' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Intérim</h2>
            <p className="text-sm text-gray-600 mb-4">
              Pendant la période choisie, cette personne valide l'étape Direction à votre place.
            </p>
            <form onSubmit={handleInterimSubmit}>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="interimUserId">
                  Intérimaire
                </label>
                <select
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  id="interimUserId"
                  name="userId"
                  value={interim.userId}
                  onChange={handleInterimChange}
                  required
                >
                  <option value="">Sélectionner un utilisateur</option>
                  {candidates.map((candidate) => (
                    <option key={candidate._id} value={candidate._id}>
                      {candidate.name} — {candidate.email}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="interimStartDate">
                    Début
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="interimStartDate"
                    type="date"
                    name="startDate"
                    value={interim.startDate}
                    onChange={handleInterimChange}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2" htmlFor="interimEndDate">
                    Fin
                  </label>
                  <input
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    id="interimEndDate"
                    type="date"
                    name="endDate"
                    value={interim.endDate}
                    onChange={handleInterimChange}
                    min={interim.startDate || undefined}
                    required
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-2">
                <button
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  type="button"
                  onClick={handleInterimClear}
                  disabled={loading}
                >
                  Retirer
                </button>
                <button
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;
